import React, { useEffect, useRef, useState } from 'react';
import 'App.css';
import CQMainScreen from 'components/cq-main-screen';
import CQLoginScreen from 'components/cq-login-screen';
import CQSessionExpiryModal from 'components/cq-session-expiry-modal';
import { AuthConsumer } from 'contexts/auth-context';
import { SFConnection } from 'common/sf-connector';
import CQStencil from 'components/cq-stencil';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import CQSFAuthCallback from './components/cq-sf-auth-callback';
import CQPushPortalPage from './components/cq-push-portal-page';
import CQPushPortalCallback from './components/cq-push-portal-callback';
import { PushPortalServiceProvider } from 'services/push-portal.service';
import CQMigrationLogin from 'components/cq-migration-login';

function App() {
  const authContext = AuthConsumer();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState<boolean>(false);
  // Ref to track the current authentication state dynamically
  const isAuthenticatedRef = useRef(authContext.isAuthenticated);

  useEffect(() => {
    isAuthenticatedRef.current = authContext.isAuthenticated;
  }, [authContext.isAuthenticated]);

  /**
   * Verifies authentication status on mount and registers a listener
   * for 'session-expired' events to handle session termination.
   */
  useEffect(() => {
    checkAuthentication();

    // Listen for session expiry events from axios interceptor
    const handleSessionExpired = () => {
      console.log('[App] Session expired event received');
      // Only show modal if user is currently authenticated
      if (isAuthenticatedRef.current) {
        console.log('[App] Showing session expiry modal');
        setShowSessionExpiredModal(true);
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, []);

  /**
   * checkAuthentication
   *
   * Sends a request to the backend to verify whether the stored Salesforce
   * access token is still valid. If valid, logs the user in via AuthContext.
   *
   * @async
   * @returns {Promise<void>}
  */
  async function checkAuthentication(){
      let response = await SFConnection.checkAuth(); 
      if(response && response.status === 200){
        authContext.login();
      }
      setIsLoading(false);  
  }

  /**
   * Handles the login action when session expires.
   * Logs out the user and shows the login screen.
   */
  async function handleSessionExpiredLogin() {
    authContext.logout();
    setShowSessionExpiredModal(false);
    try {
      await SFConnection.logOut();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  if (isLoading) {
    return <CQStencil />;
  }

  return (
    <>
      <Routes>
        <Route path="/oauth2" element={<CQSFAuthCallback />} />
        <Route path="/migration-login" element={<CQMigrationLogin />} />
        <Route element={<RequirePushPortalPermission />}>
          <Route path="/push-portal" element={<CQPushPortalPage />} />
          <Route path="/push-portal-callback" element={<CQPushPortalCallback />} />
        </Route>
        <Route path="*" element={
          <>
            <CQMainScreen />
            <CQLoginScreen />
          </>
        } />
      </Routes>
      <CQSessionExpiryModal
        isOpen={showSessionExpiredModal}
        onLogin={handleSessionExpiredLogin}
      />
    </>
  );
}

/* Guard to check auth and verify push-portal permission */
function RequirePushPortalPermission() {
  const authContext = AuthConsumer();
  const [canView, setCanView] = useState<boolean | null>(null);

  useEffect(() => {
    setCanView(null); //This prevents stale canView=true from a previous session
    if (!authContext.isAuthenticated) return;
    let active = true; // Flag to ignore the promise if the user logs out before it resolves

    PushPortalServiceProvider.getPermissions()
      .then((permissions) => {
        if (active) {
          setCanView(permissions.canView); // Only write if this is still the active request
        }
      })
      .catch(() => {
        if (active) {
          setCanView(false); // Fail closed
        }
      });

    // Cleanup: mark this request as stale so its .then() is skipped
    return () => {
      active = false;
    };
  }, [authContext.isAuthenticated]);

  if (!authContext.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (canView === null) {
    return <CQStencil />; // loading
  }

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

export default App;
